// Fully procedural Venus cloud-top shader. No textures — every cloud
// streak, polar collar, super-rotation eddy, and Y-shape chevron is
// computed per fragment from FBM + domain warp + OKLCh perceptual
// mixing. Replaces the texture-based MeshPhysicalMaterial that ran
// venus on the generic "hazy" preset.
//
// Reference techniques:
//   - Iñigo Quilez 2008, "Domain Warping" (the q/r warp pattern)
//   - Perlin / Stefan Gustavson simplex noise basis
//   - Bujack et al. 2025, OKLCh non-Riemannian colour interpolation
//
// Look target: Venus in visible light, as MESSENGER and Mariner 10 saw it
// in true colour: a pale cream disc, among the brightest objects in the
// sky, with almost no contrast. The dark Y and the mid-latitude bands
// are what the ultraviolet cameras record (an unknown UV absorber); in
// visible light they are faint, so they are kept only as hints. The disc
// darkens toward the limb (Minnaert), the thick atmosphere carries a
// soft twilight past the terminator, and the haze above the clouds
// shows as a thin bright rim that lights up round the whole limb at
// crescent phase, when it scatters sunlight forward.
//
// Visual hierarchy:
//   1. Three broad latitude zones (equatorial bright + mid belts +
//      polar collar). Venus does NOT show Jupiter-like striping; the
//      band count is intentionally low so the disc reads as a few
//      smeared cloud zones, not a candy stripe.
//   2. Super-rotation drift at ~60x the surface rate, cos²(lat)
//      modulated so equator laps the poles (Venus's atmosphere
//      genuinely circles every ~4 Earth days vs the 243-day surface
//      rotation — this is the "alive" tell that distinguishes Venus
//      visually).
//   3. Quilez double domain warp for the swirly cloud festoons.
//   4. A drifting "Y-chevron" feature — the UV-imaged pattern that
//      Mariner 10 and Akatsuki capture as two diagonal dark wedges
//      converging at the equator. Confined to ±35° latitude and
//      drifting with the super-rotation.
//   5. A polar collar — distinct darker ring at |lat| > 65° matching
//      Pioneer Venus / Magellan polar vortex shadowing.
//   6. Heavy limb darkening + warm sulphuric rim glow.

import { OKLAB_MIX_GLSL } from './oklab.js';

export const venusVertexShader = `
  varying vec3 vLocalNormal;
  varying vec3 vWorldNormal;
  varying vec3 vWorldPos;

  void main() {
    vLocalNormal = normalize(normal);
    vWorldNormal = normalize(mat3(modelMatrix) * normal);
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorldPos = wp.xyz;
    gl_Position = projectionMatrix * viewMatrix * wp;
  }
`;

export const venusFragmentShader = `
  precision highp float;

  varying vec3 vLocalNormal;
  varying vec3 vWorldNormal;
  varying vec3 vWorldPos;

  uniform float uTime;
  uniform vec3 uSunDir;
  uniform vec3 uPaletteDeep;
  uniform vec3 uPaletteBelt;
  uniform vec3 uPaletteZone;
  uniform float uBandCount;
  uniform float uTurbulence;
  uniform float uDriftRate;
  uniform float uYStrength;
  uniform float uPolarCollarStrength;
  uniform float uLimbDarken;
  uniform float uRimGlow;
  uniform float uCloudDetailStrength;
  uniform float uCloudDetailFreq;
  // Per-planet sunlight multiplier (shared uniform contract; main.js
  // wires it). <= 0.001 means "unwired" → fall back to 1.0.
  uniform float uSunIntensity;

  ${OKLAB_MIX_GLSL}

  // Simplex 3D noise — same primitive the sun + gas giant shaders use
  // so the visual character matches the rest of the scene.
  vec3 _mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 _mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 _permute(vec4 x) { return _mod289(((x * 34.0) + 1.0) * x); }
  vec4 _taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

  float snoise(vec3 v) {
    const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
    vec3 i = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);
    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;
    i = _mod289(i);
    vec4 p = _permute(_permute(_permute(
              i.z + vec4(0.0, i1.z, i2.z, 1.0))
            + i.y + vec4(0.0, i1.y, i2.y, 1.0))
            + i.x + vec4(0.0, i1.x, i2.x, 1.0));
    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;
    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);
    vec4 x = x_ * ns.x + ns.yyyy;
    vec4 y = y_ * ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);
    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);
    vec4 s0 = floor(b0) * 2.0 + 1.0;
    vec4 s1 = floor(b1) * 2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));
    vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);
    vec4 norm = _taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
    p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
  }

  float fbm(vec3 p) {
    float v = 0.0;
    float amp = 0.5;
    for (int i = 0; i < 5; i++) {
      v += amp * snoise(p);
      p *= 2.02;
      amp *= 0.5;
    }
    return v;
  }

  float warpedFbm(vec3 p, float warpAmount) {
    vec3 q = vec3(
      fbm(p + vec3(0.0, 0.0, 0.0)),
      fbm(p + vec3(5.2, 1.3, 0.0)),
      fbm(p + vec3(1.7, 9.2, 0.0))
    );
    vec3 r = vec3(
      fbm(p + warpAmount * q + vec3(1.7, 9.2, 0.0)),
      fbm(p + warpAmount * q + vec3(8.3, 2.8, 0.0)),
      fbm(p + warpAmount * q + vec3(3.5, 6.1, 0.0))
    );
    return fbm(p + warpAmount * r);
  }

  void main() {
    vec3 n = normalize(vLocalNormal);
    float lat = asin(clamp(n.y, -1.0, 1.0));
    float lon = atan(n.z, n.x);

    // Super-rotation as a Y-axis rotation of the sample normal,
    // scaled by cos²(lat) so the equator drifts fastest and the poles
    // stay locked. Same anti-seam pattern as the gas giant shader —
    // the noise inputs only ever see the rotated 3D normal, never
    // raw lon, so the antimeridian is seamless.
    float driftAngle = uTime * uDriftRate * cos(lat) * cos(lat);
    float dc = cos(driftAngle);
    float ds = sin(driftAngle);
    vec3 nDrift = vec3(
      n.x * dc - n.z * ds,
      n.y,
      n.x * ds + n.z * dc
    );

    // Domain-warped band lookup. Venus's band count is intentionally
    // low (3-5) so the disc reads as a few broad zones, not a striped
    // Jupiter pattern.
    vec3 warpInput = nDrift * 1.4 + vec3(0.0, 0.0, uTime * 0.04);
    float warpedBand = warpedFbm(warpInput, uTurbulence);
    float latWarped = lat + warpedBand * 0.12;

    // Wider latitude meander than Jupiter — Venus's zone boundaries
    // are diffuse, not razor-edged. Pushing chaos hard so the
    // boundaries between equatorial zone, mid-belt, and polar collar
    // never line up as horizontal stripes.
    vec3 chaosInput = nDrift * 1.1;
    float bigEddies = fbm(chaosInput);
    float bandBrightnessMod = fbm(chaosInput + vec3(3.7, 1.2, 5.6));
    latWarped += bigEddies * 0.14;

    float bandRaw = sin(latWarped * uBandCount) * 0.5 + 0.5;
    float bandStrength = 0.55 + bandBrightnessMod * 0.55;
    float band = mix(0.5, bandRaw, bandStrength);

    // Three-stop OKLCh palette mix. Sulphuric cream is a low-chroma
    // warm ramp; a straight linear-sRGB mix between any two of these
    // would land in muddy beige. OKLCh keeps the saturation up
    // through the mid-tones.
    vec3 base = oklchMix(uPaletteDeep, uPaletteBelt, smoothstep(0.0, 0.5, band));
    base = oklchMix(base, uPaletteZone, smoothstep(0.4, 1.0, band));

    // Y-chevron feature. Venus's UV-imaged signature is two diagonal
    // dark wedges that converge at the equator and drift westward
    // with the super-rotation. Implemented by computing distance to
    // a "V" SDF in (drifted-lon, lat) space, confined to equatorial
    // latitudes so the polar collar isn't disturbed.
    //
    // Drift the Y at the same equatorial super-rotation rate so it
    // appears glued to the band system, not floating on top.
    float yLon = lon + driftAngle;
    // Wrap into [-PI, PI] so the chevron doesn't double at the antimeridian.
    if (yLon >  3.14159265) yLon -= 6.28318530;
    if (yLon < -3.14159265) yLon += 6.28318530;
    float absLat = abs(lat);
    // Two diagonal arms: |yLon - lat * slope| picks distance to a
    // line through the origin sloping up-right, the other arm sloped
    // up-left. The minimum is the V/Y wedge distance.
    float armA = abs(yLon + lat * 2.2);
    float armB = abs(yLon - lat * 2.2);
    float yWedge = min(armA, armB);
    // Confine to ±35° latitude (~0.6 rad), feather smoothly.
    float yLatMask = 1.0 - smoothstep(0.45, 0.70, absLat);
    // Darken inside the wedge. Inner edge is sharp, outer edge soft
    // so it reads as cloud structure, not a drawn line.
    float yWedgeMask = (1.0 - smoothstep(0.10, 0.55, yWedge)) * yLatMask;
    // FBM perturbation so the wedge edges break up into cloud streaks
    // rather than appearing as a clean geometric V.
    float yJitter = fbm(nDrift * 6.0 + vec3(uTime * 0.05, 0.0, 0.0));
    yWedgeMask *= 0.65 + 0.35 * yJitter;
    base = oklchMix(base, uPaletteDeep, yWedgeMask * uYStrength * 0.55);

    // Polar collar. Venus's poles are notably darker in space imagery
    // due to the polar vortex creating a thicker / shadowed cloud
    // structure. Smooth onset at |lat| > 65° (~1.13 rad), full effect
    // by |lat| > 80° (~1.40 rad). Jittered by FBM so the collar
    // boundary isn't a clean latitude line.
    float collarMask = smoothstep(1.05, 1.40, absLat);
    float collarJitter = fbm(nDrift * 3.2 + vec3(11.0, 2.0, uTime * 0.02));
    collarMask = clamp(collarMask + collarJitter * 0.18, 0.0, 1.0);
    base = oklchMix(base, uPaletteDeep, collarMask * uPolarCollarStrength * 0.6);

    // High-frequency cloud detail — the per-fragment FBM that gives
    // the disc its multi-scale "real cloud" complexity at any zoom
    // level. Two layers at different frequencies stack a coarse
    // weather-system overlay with finer streak structure.
    float cloud = fbm(nDrift * uCloudDetailFreq + vec3(uTime * 0.02));
    float fineDetail = fbm(nDrift * (uCloudDetailFreq * 2.6) + vec3(uTime * 0.03, uTime * 0.025, uTime * 0.035));
    base += cloud * 0.045 * uCloudDetailStrength;
    base += fineDetail * 0.025 * uCloudDetailStrength;

    vec3 N = normalize(vWorldNormal);
    vec3 L = normalize(uSunDir);
    vec3 viewDir = normalize(cameraPosition - vWorldPos);
    float cosI = dot(N, L);
    float NdotL = max(0.0, cosI);
    float NdotV = max(0.0, dot(N, viewDir));

    // Minnaert limb darkening, I = mu0^k mu^(k-1): k = 1 is Lambert, and
    // uLimbDarken sets how far below 1 the cloud tops sit (0.15 gives
    // k = 0.85, the gentle darkening of the visible-light disc).
    float k = 1.0 - uLimbDarken;
    float minnaert = pow(NdotL, k) * pow(max(NdotV, 0.05), k - 1.0);
    // The terminator is soft: sunlight reaches past it through the thick
    // atmosphere, warmer as it crosses more air, and gone a few degrees on.
    float soft = smoothstep(-0.04, 0.16, cosI);
    float twilight = smoothstep(-0.16, 0.0, cosI) * (1.0 - smoothstep(0.0, 0.14, cosI));
    vec3 twilightTint = vec3(1.0, 0.78, 0.5);

    // The haze above the cloud deck: a thin rim brightest at the limb,
    // faint on the day side, strong round the whole limb when the Sun is
    // behind the planet (forward scattering at crescent phase).
    float rim = pow(1.0 - NdotV, 6.0);
    float forward = pow(max(0.0, -dot(viewDir, L)), 4.0);
    float hazeLight = rim * uRimGlow * (0.3 * smoothstep(-0.1, 0.4, cosI) + 1.6 * forward * smoothstep(-0.45, 0.1, cosI));

    // Lit clouds and haze scale with the per-planet sunlight multiplier.
    float sunI = uSunIntensity > 0.001 ? uSunIntensity : 1.0;
    vec3 lit = base * (minnaert * soft + 0.07 * twilight * twilightTint) * sunI;
    lit += vec3(1.0, 0.95, 0.84) * hazeLight * sunI;

    gl_FragColor = vec4(lit, 1.0);
  }
`;
